import React from 'react';

interface IconProps {
  size?: number | string;
  className?: string;
  color?: string;
}

// VSynth microphone icon with digital elements
export function VSynthMicIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Microphone body */}
      <rect x="9" y="2" width="6" height="10" rx="3" stroke={color} strokeWidth="2" fill="none" />
      
      {/* Digital wave pattern inside mic */}
      <path
        d="M10.5 4.5h3M10.5 6h3M10.5 7.5h3M10.5 9h3"
        stroke={color}
        strokeWidth="1"
        opacity="0.6"
      />
      
      {/* Mic stand */}
      <path d="M12 12v3" stroke={color} strokeWidth="2" />
      <path d="M8 15h8" stroke={color} strokeWidth="2" />
      
      {/* Digital signal waves */}
      <path
        d="M6 8c0-3.3 2.7-6 6-6s6 2.7 6 6M5 10c0-3.9 3.1-7 7-7s7 3.1 7 7"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
      />
      
      {/* Digital dots */}
      <circle cx="4" cy="12" r="1" fill={color} opacity="0.5" />
      <circle cx="20" cy="12" r="1" fill={color} opacity="0.5" />
    </svg>
  );
}

// Synthesizer keyboard icon
export function SynthKeyboardIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Keyboard base */}
      <rect x="2" y="8" width="20" height="10" rx="2" stroke={color} strokeWidth="2" fill="none" />
      
      {/* White keys */}
      <rect x="3" y="9" width="2.5" height="8" fill={color} opacity="0.1" />
      <rect x="6" y="9" width="2.5" height="8" fill={color} opacity="0.1" />
      <rect x="9" y="9" width="2.5" height="8" fill={color} opacity="0.1" />
      <rect x="12" y="9" width="2.5" height="8" fill={color} opacity="0.1" />
      <rect x="15" y="9" width="2.5" height="8" fill={color} opacity="0.1" />
      <rect x="18" y="9" width="2.5" height="8" fill={color} opacity="0.1" />
      
      {/* Black keys */}
      <rect x="4.5" y="9" width="1.5" height="5" fill={color} opacity="0.8" />
      <rect x="7.5" y="9" width="1.5" height="5" fill={color} opacity="0.8" />
      <rect x="13.5" y="9" width="1.5" height="5" fill={color} opacity="0.8" />
      <rect x="16.5" y="9" width="1.5" height="5" fill={color} opacity="0.8" />
      <rect x="19.5" y="9" width="1.5" height="5" fill={color} opacity="0.8" />
      
      {/* Digital display */}
      <rect x="4" y="4" width="16" height="3" rx="1" stroke={color} strokeWidth="1" fill="none" />
      <path d="M6 5.5h2M10 5.5h4M16 5.5h2" stroke={color} strokeWidth="1" opacity="0.6" />
    </svg>
  );
}

// Waveform with digital elements
export function DigitalWaveIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Main waveform */}
      <path
        d="M2 12h2l1-4 2 8 2-6 2 4 2-3 2 2 2-1 2 3 2-2 2 1h1"
        stroke={color}
        strokeWidth="2"
        fill="none"
      />
      
      {/* Digital grid */}
      <defs>
        <pattern id="grid" width="4" height="4" patternUnits="userSpaceOnUse">
          <path d="M 4 0 L 0 0 0 4" fill="none" stroke={color} strokeWidth="0.5" opacity="0.3" />
        </pattern>
      </defs>
      <rect width="24" height="24" fill="url(#grid)" />
      
      {/* Digital markers */}
      <circle cx="6" cy="8" r="1" fill={color} opacity="0.6" />
      <circle cx="12" cy="16" r="1" fill={color} opacity="0.6" />
      <circle cx="18" cy="10" r="1" fill={color} opacity="0.6" />
    </svg>
  );
}

// Virtual singer/character icon
export function VirtualSingerIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Head */}
      <circle cx="12" cy="8" r="4" stroke={color} strokeWidth="2" fill="none" />
      
      {/* Digital face elements */}
      <circle cx="10" cy="7" r="0.5" fill={color} />
      <circle cx="14" cy="7" r="0.5" fill={color} />
      <path d="M10 9.5c1 1 3 1 4 0" stroke={color} strokeWidth="1.5" fill="none" />
      
      {/* Body */}
      <path d="M12 12v6" stroke={color} strokeWidth="2" />
      <path d="M8 15l4-1 4 1" stroke={color} strokeWidth="2" fill="none" />
      
      {/* Digital aura/effects */}
      <path
        d="M7 6c-1-1-1-3 0-4M17 6c1-1 1-3 0-4M6 10c-2 0-3-1-3-2M18 10c2 0 3-1 3-2"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
      />
      
      {/* Sound waves */}
      <path
        d="M4 12c0-2 1-3 2-3M20 12c0-2-1-3-2-3M3 14c0-3 2-5 3-5M21 14c0-3-2-5-3-5"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.4"
      />
    </svg>
  );
}

// Digital music note with circuit elements
export function DigitalNoteIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Note head */}
      <ellipse cx="6" cy="16" rx="2" ry="1.5" fill={color} />
      
      {/* Note stem */}
      <path d="M8 16V6" stroke={color} strokeWidth="2" />
      
      {/* Digital flag */}
      <path
        d="M8 6c4 0 6 2 8 1V12c-2 1-4-1-8-1"
        fill={color}
        opacity="0.8"
      />
      
      {/* Circuit pattern on flag */}
      <path
        d="M10 8h2M12 9h2M10 10h2"
        stroke="white"
        strokeWidth="0.5"
        opacity="0.6"
      />
      
      {/* Digital connections */}
      <circle cx="18" cy="8" r="1" fill={color} opacity="0.6" />
      <circle cx="20" cy="10" r="0.5" fill={color} opacity="0.4" />
      <path d="M18 8l2 2" stroke={color} strokeWidth="1" opacity="0.5" />
      
      {/* Binary pattern */}
      <text x="2" y="4" fontSize="3" fill={color} opacity="0.3">01</text>
      <text x="18" y="20" fontSize="3" fill={color} opacity="0.3">10</text>
    </svg>
  );
}

// Vocal synthesis icon
export function VocalSynthIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Mouth/vocal cavity */}
      <ellipse cx="12" cy="12" rx="8" ry="4" stroke={color} strokeWidth="2" fill="none" />
      
      {/* Sound waves emanating */}
      <path
        d="M12 8c-2 0-4 1-4 4s2 4 4 4M12 8c2 0 4 1 4 4s-2 4-4 4"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        opacity="0.7"
      />
      
      {/* Digital frequency bars */}
      <rect x="10" y="10" width="1" height="4" fill={color} opacity="0.8" />
      <rect x="12" y="9" width="1" height="6" fill={color} opacity="0.8" />
      <rect x="14" y="10.5" width="1" height="3" fill={color} opacity="0.8" />
      
      {/* Outer sound waves */}
      <path
        d="M6 8c-2 1-3 3-3 4s1 3 3 4M18 8c2 1 3 3 3 4s-1 3-3 4"
        stroke={color}
        strokeWidth="1"
        fill="none"
        opacity="0.5"
      />
      
      {/* Digital particles */}
      <circle cx="8" cy="6" r="0.5" fill={color} opacity="0.4" />
      <circle cx="16" cy="6" r="0.5" fill={color} opacity="0.4" />
      <circle cx="8" cy="18" r="0.5" fill={color} opacity="0.4" />
      <circle cx="16" cy="18" r="0.5" fill={color} opacity="0.4" />
    </svg>
  );
}

// AI/Neural network music icon
export function AIMusicsIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Neural network nodes */}
      <circle cx="6" cy="6" r="2" stroke={color} strokeWidth="1.5" fill="none" />
      <circle cx="18" cy="6" r="2" stroke={color} strokeWidth="1.5" fill="none" />
      <circle cx="6" cy="18" r="2" stroke={color} strokeWidth="1.5" fill="none" />
      <circle cx="18" cy="18" r="2" stroke={color} strokeWidth="1.5" fill="none" />
      <circle cx="12" cy="12" r="2.5" stroke={color} strokeWidth="2" fill="none" />
      
      {/* Connections */}
      <path d="M8 6l6 6M16 6l-6 6M8 18l6-6M16 18l-6-6" stroke={color} strokeWidth="1" opacity="0.6" />
      
      {/* Music note in center */}
      <ellipse cx="11" cy="13" rx="1" ry="0.8" fill={color} />
      <path d="M12 13V10" stroke={color} strokeWidth="1.5" />
      <path d="M12 10c1 0 2 0.5 2 1" stroke={color} strokeWidth="1" fill="none" />
      
      {/* Data flow indicators */}
      <circle cx="9" cy="9" r="0.5" fill={color} opacity="0.7" />
      <circle cx="15" cy="9" r="0.5" fill={color} opacity="0.7" />
      <circle cx="9" cy="15" r="0.5" fill={color} opacity="0.7" />
      <circle cx="15" cy="15" r="0.5" fill={color} opacity="0.7" />
    </svg>
  );
}

// Tuning/pitch adjustment icon
export function PitchTuneIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Tuning fork base */}
      <path d="M11 12v8" stroke={color} strokeWidth="2" />
      <path d="M13 12v8" stroke={color} strokeWidth="2" />
      <path d="M9 20h6" stroke={color} strokeWidth="2" />
      
      {/* Tuning fork prongs */}
      <path d="M11 12V4c0-1 0-2-2-2s-2 1-2 2v6" stroke={color} strokeWidth="2" fill="none" />
      <path d="M13 12V4c0-1 0-2 2-2s2 1 2 2v6" stroke={color} strokeWidth="2" fill="none" />
      
      {/* Vibration waves */}
      <path
        d="M5 8c-1 0-2-0.5-2-1s1-1 2-1M19 8c1 0 2-0.5 2-1s-1-1-2-1"
        stroke={color}
        strokeWidth="1"
        opacity="0.6"
      />
      <path
        d="M4 10c-1.5 0-2.5-0.5-2.5-1s1-1 2.5-1M20 10c1.5 0 2.5-0.5 2.5-1s-1-1-2.5-1"
        stroke={color}
        strokeWidth="1"
        opacity="0.4"
      />
      
      {/* Digital frequency display */}
      <rect x="8" y="14" width="8" height="2" rx="1" stroke={color} strokeWidth="1" fill="none" />
      <path d="M10 15h1M13 15h1" stroke={color} strokeWidth="1" opacity="0.6" />
    </svg>
  );
}

// Voice bank/library icon
export function VoiceBankIcon({ size = 24, className = '', color = 'currentColor' }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {/* Library shelves */}
      <rect x="3" y="4" width="18" height="2" fill={color} opacity="0.8" />
      <rect x="3" y="9" width="18" height="2" fill={color} opacity="0.8" />
      <rect x="3" y="14" width="18" height="2" fill={color} opacity="0.8" />
      <rect x="3" y="19" width="18" height="2" fill={color} opacity="0.8" />
      
      {/* Voice containers/books */}
      <rect x="4" y="6" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="8" y="6" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="12" y="6" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="16" y="6" width="3" height="3" fill={color} opacity="0.6" />
      
      <rect x="4" y="11" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="8" y="11" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="12" y="11" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="16" y="11" width="3" height="3" fill={color} opacity="0.6" />
      
      <rect x="4" y="16" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="8" y="16" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="12" y="16" width="3" height="3" fill={color} opacity="0.6" />
      <rect x="16" y="16" width="3" height="3" fill={color} opacity="0.6" />
      
      {/* Digital indicators */}
      <circle cx="5.5" cy="7.5" r="0.3" fill="white" />
      <circle cx="9.5" cy="7.5" r="0.3" fill="white" />
      <circle cx="13.5" cy="7.5" r="0.3" fill="white" />
      <circle cx="17.5" cy="7.5" r="0.3" fill="white" />
      
      {/* Frame */}
      <rect x="2" y="3" width="20" height="18" stroke={color} strokeWidth="2" fill="none" rx="1" />
    </svg>
  );
}

// Export all icons as a collection
export const VSynthIcons = {
  VSynthMic: VSynthMicIcon,
  SynthKeyboard: SynthKeyboardIcon,
  DigitalWave: DigitalWaveIcon,
  VirtualSinger: VirtualSingerIcon,
  DigitalNote: DigitalNoteIcon,
  VocalSynth: VocalSynthIcon,
  AIMusic: AIMusicsIcon,
  PitchTune: PitchTuneIcon,
  VoiceBank: VoiceBankIcon,
};
 